import { createSelector } from 'reselect'
import Immutable from 'immutable'
import { identity, EMPTY_IMMUTABLE_MAP } from '@lacele/const'
import { makePath } from '@lacele/utils'
import { createStateSelector } from '../Redux/utils'
import {
    FORM_SUBMIT,
    FORM_FIELDS_VALUE,
    FORM_FIELD_REMOVE
} from './types'
import {
    createDataTargetSelector,
    createMetaTargetSelector,
    createStateTargetSelector,
    createSubmitTargetSelector,
    isDataNotEmpty,
    isValueSet
} from './utils'

const getDataTarget = createDataTargetSelector()
const getMetaTarget = createMetaTargetSelector()
const getFieldsStateTarget = createStateTargetSelector()
const getSubmitTarget = createSubmitTargetSelector()

export const defaultState = Immutable.Map({
    [getDataTarget()]: EMPTY_IMMUTABLE_MAP,
    [getMetaTarget()]: EMPTY_IMMUTABLE_MAP
})

export default (state, action) => {
    let actualState = state || defaultState
    switch (action.type) {
        case FORM_SUBMIT:
            return actualState.set(getSubmitTarget(), true)
        case FORM_FIELDS_VALUE:
            actualState = actualState.delete(getSubmitTarget())
            if (action.payload) {
                if (action.payload.data) {
                    actualState = actualState.mergeIn([getDataTarget()], action.payload.data)
                }
                if (action.payload.meta) {
                    Object.keys(action.payload.meta).forEach((fieldName) => {
                        actualState = actualState.mergeIn(
                            [getMetaTarget(), fieldName],
                            action.payload.meta[fieldName]
                        )
                    })
                }
            }
            return actualState
        case FORM_FIELD_REMOVE:
            actualState = actualState.delete(getSubmitTarget())
            if (Array.isArray(action.payload)) {
                action.payload.forEach((fieldName) => {
                    actualState = actualState.deleteIn([getDataTarget()].concat(fieldName))
                        .deleteIn([getMetaTarget()].concat(fieldName))
                })
                return actualState
            }
            if (action.payload) {
                return actualState.deleteIn([getDataTarget()].concat(action.payload))
                    .deleteIn([getMetaTarget()].concat(action.payload))
            }
            return actualState.delete(getDataTarget())
                .delete(getMetaTarget())
        default:
            return actualState
    }
}

// Selector Creators

export const createDataSelector = () => createSelector(
    identity,
    (state) => state.get(getDataTarget()) || EMPTY_IMMUTABLE_MAP
)

export const createMetaSelector = () => createSelector(
    identity,
    (state) => state.get(getMetaTarget()) || EMPTY_IMMUTABLE_MAP
)

export const createMetaValueSelector = () => createSelector(
    createMetaSelector(),
    (_, { name }) => name,
    (meta, name) => meta.get(name)
)

export const createPaginatorFilterSelector = () => createSelector(
    createDataSelector(),
    (data) => ({
        rowsPerPage: data.get('size') || 20,
        page: data.get('page') || 0
    })
)

export const createFieldsStateSelector = () => createSelector(
    identity,
    (state) => state.get(getFieldsStateTarget()) || EMPTY_IMMUTABLE_MAP
)

export const createFieldTargetSelector = () => createSelector(
    (_, { name }) => name,
    (_, { storeTarget }) => storeTarget,
    (name, storeTarget) => makePath(storeTarget, getDataTarget(), name)
)

export const createFieldStateTargetSelector = () => createSelector(
    (_, { name }) => name,
    (_, { storeTarget }) => storeTarget,
    (name, storeTarget) => makePath(storeTarget, getFieldsStateTarget(), name)
)

export const createFieldStateSelector = () => createSelector(
    createFieldsStateSelector(),
    (_, { name }) => name,
    (fieldsState, name) => fieldsState.get(name) || EMPTY_IMMUTABLE_MAP
)

export const createIsNotEmptySelector = () => createSelector(
    createDataSelector(),
    isDataNotEmpty
)

/**
 * @deprecated
 */
export const getData = createDataSelector()

/**
 * @deprecated
 */
export const getMeta = createMetaSelector()

/**
 * @deprecated
 */
export const getFieldsState = createFieldsStateSelector()

/**
 * @deprecated
 */
export const getMetaValue = createMetaValueSelector()

/**
 * @deprecated
 */
export const getFieldState = createFieldStateSelector()

/**
 * @deprecated
 */
export const isNotEmpty = createIsNotEmptySelector()

// Input field utilities

export const createValueByNameSelector = () => createSelector(
    (_, { pristineData }) => pristineData,
    (_, __, name) => name,
    createSelector(
        createDataSelector(),
        (_, __, name) => name,
        (data, name) => data.get(name)
    ),
    (pristineData, name, value) => {
        if (isValueSet(value)) {
            return value
        }
        if (pristineData) {
            return pristineData[name]
        }
        return undefined
    }
)

export const createErrorByNameSelector = () => createSelector(
    (_, { errorData }) => errorData,
    (_, { name }) => name,
    (erroData, name) => (erroData ? erroData[name] : undefined)
)

export const createValueSelector = () => createSelector(
    identity,
    (_, props) => props,
    (_, { name }) => name,
    createValueByNameSelector()
)

/**
 * @deprecated
 */
export const getValueByName = createValueByNameSelector()

/**
 * @deprecated
 */
export const getValue = createValueSelector()

// Client utilities

export const createTargetDataSelector = (getTargetFunc) => createSelector(
    createStateSelector(getTargetFunc),
    createDataSelector()
)

export const createTargetMetaSelector = (getTargetFunc) => createSelector(
    createStateSelector(getTargetFunc),
    createMetaSelector()
)

export const createTargetPaginatorFilterSelector = (getTargetFunc) => createSelector(
    createStateSelector(getTargetFunc),
    createPaginatorFilterSelector()
)

export const createFormValueSelector
    = (getTargetFunc, pristineDataSelector, name) => createSelector(
        pristineDataSelector,
        createTargetDataSelector(getTargetFunc),
        (pristineData, data) => {
            const value = data.get(name)
            if (isValueSet(value)) {
                return value
            }
            return pristineData ? pristineData[name] : undefined
        }
    )
